import {
  EntityConfig,
  EntityDataInputConfig,
  EntityDetectionRoutingModeType,
  EntityDetectConfig,
  EntityIdentifierContract,
  EntityIdentifierRepresentationType,
  EntityModeType,
  EntityProvidedConfig,
  ExtractDataStep,
} from '@/types/automationSteps.ts';
import { Group, JsonInput, Stack } from '@mantine/core';
import { DataInput } from '@/types/automationFields.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import InputLabelWithHoverCard from '@/components/InputLabelWithHoverCard.tsx';
import { emptyDataInput } from '@/pages/Editor/types.ts';
import DataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/DataInputSelectorUI.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import { STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';

type props = {
  step: ExtractDataStep;
};

type Fields = {
  extract_entities: boolean;
  entity_mode: EntityModeType;
  entity_config: EntityConfig;
  supports_long_entity_extraction: boolean;
  entity_detection_routing_mode: EntityDetectionRoutingModeType;
  min_extract_count: number | 'Any';
  max_extract_count: number | 'Any';
};

type FieldPatch = Partial<Fields>;

const ENTITY_DETECTION_ROUTING_MODE_OPTIONS = [
  { value: EntityDetectionRoutingModeType.Infer, label: 'Infer from scope' },
  { value: EntityDetectionRoutingModeType.DelegatedOnly, label: 'Force delegated' },
  { value: EntityDetectionRoutingModeType.PreferDirect, label: 'Prefer direct' },
];

const IDENTIFIER_REPRESENTATION_OPTIONS = [
  { value: EntityIdentifierRepresentationType.PlainString, label: 'Plain string' },
  { value: EntityIdentifierRepresentationType.JsonObjectString, label: 'JSON object string' },
];

const DEFAULT_IDENTIFIER_CONTRACT: EntityIdentifierContract = {
  representation: EntityIdentifierRepresentationType.PlainString,
  required_object_keys: [],
  allow_additional_object_keys: true,
};

const getEntityDetectConfig = (entityConfig: EntityConfig): EntityDetectConfig => {
  const detectConfig = entityConfig as Partial<EntityDetectConfig>;
  return {
    entity_description: detectConfig.entity_description ?? '',
    identifier_contract: {
      representation:
        detectConfig.identifier_contract?.representation ??
        DEFAULT_IDENTIFIER_CONTRACT.representation,
      required_object_keys:
        detectConfig.identifier_contract?.required_object_keys ??
        DEFAULT_IDENTIFIER_CONTRACT.required_object_keys,
      allow_additional_object_keys:
        detectConfig.identifier_contract?.allow_additional_object_keys ??
        DEFAULT_IDENTIFIER_CONTRACT.allow_additional_object_keys,
    },
  };
};

const detectConfigNeedsNormalization = (entityConfig: EntityConfig): boolean => {
  const detectConfig = entityConfig as Partial<EntityDetectConfig>;
  return !detectConfig.identifier_contract;
};

const getDefaultEntityConfig = (entity_mode: EntityModeType): EntityConfig => {
  switch (entity_mode) {
    case EntityModeType.DataInput:
      return { data_input: emptyDataInput };
    case EntityModeType.Provided:
      return { values: [] };
    case EntityModeType.Detect:
      return {
        entity_description: '',
        identifier_contract: { ...DEFAULT_IDENTIFIER_CONTRACT },
      };
  }
};

const ExtractMetadataFields = ({ step }: props) => {
  const { fields, onChange } = useUpdateStepFields<Fields>({ step });
  const detectConfig =
    fields.entity_mode === EntityModeType.Detect ? getEntityDetectConfig(fields.entity_config) : null;
  const identifierContract = detectConfig?.identifier_contract ?? null;
  const shouldPersistNormalizedDetectConfig =
    fields.entity_mode === EntityModeType.Detect && detectConfigNeedsNormalization(fields.entity_config);

  const onChangeWithDetectConfigNormalization = (patch: FieldPatch) => {
    if (
      shouldPersistNormalizedDetectConfig &&
      detectConfig &&
      patch.entity_mode !== EntityModeType.DataInput &&
      patch.entity_mode !== EntityModeType.Provided &&
      patch.entity_config === undefined
    ) {
      onChange({
        ...patch,
        entity_config: detectConfig,
      });
      return;
    }

    onChange(patch);
  };

  const entityEmptyDescription = useStepSpecificError(
    step,
    StepErrorType.ExtractEntityEmptyDescription,
  );
  const entityInvalidDataInput = useStepSpecificError(
    step,
    StepErrorType.ExtractEntityInvalidDataInput,
  );
  const entityInvalidProvidedValues = useStepSpecificError(
    step,
    StepErrorType.ExtractEntityInvalidProvidedValues,
  );
  const entityInvalidIdentifierContract = useStepSpecificError(
    step,
    StepErrorType.ExtractEntityInvalidIdentifierContract,
  );
  const entityInvalidRoutingMode = useStepSpecificError(
    step,
    StepErrorType.ExtractEntityInvalidRoutingMode,
  );

  const extractInvalidExtractCount = useStepSpecificError(
    step,
    StepErrorType.ExtractInvalidExtractCount,
  );

  return (
    <Stack w={'100%'}>
      <Group justify="space-between" align="center" pt="sm">
        <StyledInputLabel size="sm">Extract Entities?</StyledInputLabel>
        <StyledSwitch
          aria-label="Extract Entities?"
          checked={fields.extract_entities}
          onChange={(event) =>
            onChangeWithDetectConfigNormalization({
              extract_entities: event.currentTarget.checked,
              ...(event.currentTarget.checked
                ? {}
                : {
                    entity_detection_routing_mode: EntityDetectionRoutingModeType.Infer,
                  }),
            })
          }
        />
      </Group>

      {fields.extract_entities && (
        <>
          <StyledSelect
            label={
              <InputLabelWithHoverCard
                label={'Entity Mode'}
                hoverText={'Where to extract the entities from?'}
              />
            }
            placeholder="Select Entity Mode"
            aria-label="Entity Mode"
            data={Object.values(EntityModeType).map((type) => ({ value: type, label: type }))}
            value={fields.entity_mode}
            onChange={(value) => {
              if (!value) {
                return;
              }
              const entity_config =
                value !== fields.entity_mode
                  ? getDefaultEntityConfig(value as EntityModeType)
                  : fields.entity_config;
              onChangeWithDetectConfigNormalization({
                entity_mode: value as EntityModeType,
                entity_config: entity_config,
                ...(value === EntityModeType.Detect
                  ? {}
                  : {
                      entity_detection_routing_mode: EntityDetectionRoutingModeType.Infer,
                    }),
              });
            }}
            required={true}
            allowDeselect={false}
            clearable={false}
          />

          {fields.entity_mode === EntityModeType.Detect && (
            <>
              <StyledTextarea
                label="Extraction entity description"
                description="Describe the business entity to detect and how to distinguish it from neighbors. Keep exact identifier JSON/key-shape rules in the identifier contract below, not only in prose."
                value={detectConfig?.entity_description ?? ''}
                onChange={(event) => {
                  if (!detectConfig) {
                    return;
                  }
                  onChangeWithDetectConfigNormalization({
                    entity_config: {
                      ...detectConfig,
                      entity_description: event.currentTarget.value,
                    },
                  });
                }}
                required={true}
                maxLength={STEP_LOGIC_MAX_LENGTH}
                autosize={true}
                minRows={2}
                error={entityEmptyDescription}
              />
              <StyledSelect
                label={
                  <InputLabelWithHoverCard
                    label={'Identifier Representation'}
                    hoverText={
                      'Use plain string for ordinary label-style identifiers. Use JSON object string only when downstream logic needs a structured identifier such as label plus pages.'
                    }
                  />
                }
                description="Authoritative identifier-shape contract for detect-mode entity extraction."
                aria-label="Identifier Representation"
                data={IDENTIFIER_REPRESENTATION_OPTIONS}
                value={identifierContract?.representation}
                onChange={(value) => {
                  if (!value || !detectConfig || !identifierContract) {
                    return;
                  }

                  onChangeWithDetectConfigNormalization({
                    entity_config: {
                      ...detectConfig,
                      identifier_contract: {
                        ...identifierContract,
                        representation: value as EntityIdentifierRepresentationType,
                        required_object_keys:
                          value === EntityIdentifierRepresentationType.JsonObjectString
                            ? identifierContract.required_object_keys
                            : [],
                      },
                    },
                  });
                }}
                required={true}
                allowDeselect={false}
                clearable={false}
              />
              {identifierContract?.representation ===
                EntityIdentifierRepresentationType.JsonObjectString && (
                <>
                  <StyledTextarea
                    label="Required identifier object keys"
                    description="Enter one key per line, for example `label` and `pages`. Use this only when every identifier must be a JSON object string with these required keys."
                    value={identifierContract.required_object_keys.join('\n')}
                    placeholder={'label\npages'}
                    onChange={(event) => {
                      if (!detectConfig || !identifierContract) {
                        return;
                      }
                      const requiredObjectKeys = event.currentTarget.value
                        .split('\n')
                        .map((value) => value.trim())
                        .filter(Boolean);
                      onChangeWithDetectConfigNormalization({
                        entity_config: {
                          ...detectConfig,
                          identifier_contract: {
                            ...identifierContract,
                            required_object_keys: requiredObjectKeys,
                          },
                        },
                      });
                    }}
                    autosize={true}
                    minRows={2}
                  />
                  <StyledSwitch
                    label="Allow additional identifier object keys"
                    description="Disable this only when retries and final merged submission should reject any keys beyond the required list above."
                    checked={identifierContract.allow_additional_object_keys}
                    onChange={(event) => {
                      if (!detectConfig || !identifierContract) {
                        return;
                      }
                      onChangeWithDetectConfigNormalization({
                        entity_config: {
                          ...detectConfig,
                          identifier_contract: {
                            ...identifierContract,
                            allow_additional_object_keys: event.currentTarget.checked,
                          },
                        },
                      });
                    }}
                  />
                </>
              )}
              <StyledInputErrorText>{entityInvalidIdentifierContract}</StyledInputErrorText>
              <StyledSwitch
                label="Allow long-running entity extraction"
                description="Use the slower long-running job path when entity detection may exceed normal runtime limits. This is a runtime hint only and does not force direct versus delegated routing."
                checked={fields.supports_long_entity_extraction}
                onChange={(event) =>
                  onChangeWithDetectConfigNormalization({
                    supports_long_entity_extraction: event.currentTarget.checked,
                  })
                }
              />
              <StyledSelect
                label={
                  <InputLabelWithHoverCard
                    label={'Entity Routing Mode'}
                    hoverText={
                      'Advanced detect-entity routing override. Use infer by default. Force delegated only when the step must always use large-document extraction. Prefer direct only when direct review is intentionally preferred for ambiguous scopes. This is separate from the long-running runtime hint above.'
                    }
                  />
                }
                description="Advanced override for detect-entity routing. Infer is the default and should be used unless the step needs an explicit routing policy."
                aria-label="Entity Routing Mode"
                data={ENTITY_DETECTION_ROUTING_MODE_OPTIONS}
                value={fields.entity_detection_routing_mode}
                onChange={(value) => {
                  if (!value) {
                    return;
                  }
                  onChangeWithDetectConfigNormalization({
                    entity_detection_routing_mode: value as EntityDetectionRoutingModeType,
                  });
                }}
                required={true}
                allowDeselect={false}
                clearable={false}
                error={entityInvalidRoutingMode}
              />
            </>
          )}
          {fields.entity_mode === EntityModeType.DataInput && (
            <DataInputSelectorUI
              dataInput={(fields.entity_config as EntityDataInputConfig)?.data_input}
              stepId={step.step_id}
              updateDataInput={(dataInput: DataInput) =>
                onChangeWithDetectConfigNormalization({ entity_config: { data_input: dataInput } })
              }
              error={entityInvalidDataInput}
            />
          )}
          {fields.entity_mode === EntityModeType.Provided && (
            <JsonInput
              label={'Provided entity values'}
              value={JSON.stringify((fields.entity_config as EntityProvidedConfig)?.values)}
              // placeholder=""
              onChange={(value) => {
                value = value || '[]';
                try {
                  const results = JSON.parse(value);
                  if (Array.isArray(results)) {
                    onChangeWithDetectConfigNormalization({ entity_config: { values: results } });
                  }
                } catch (e) {
                  console.error(e);
                }
              }}
              placeholder="Provide the list of entities to extract"
              required={true}
              // description={JSON.stringify(selectedAction.config)}
              autosize
              validationError="Invalid JSON"
              error={entityInvalidProvidedValues}
            />
          )}

          <Stack gap={0}>
            <Group wrap={'nowrap'}>
              <StyledNumberInput
                label={
                  <InputLabelWithHoverCard
                    label={'Min Extraction Items'}
                    hoverText={
                      'The minimum number of items to extract. Leave empty to let system decide'
                    }
                  />
                }
                placeholder="Any"
                value={fields.min_extract_count === 'Any' ? '' : fields.min_extract_count}
                onChange={(newValue) => {
                  onChangeWithDetectConfigNormalization({
                    min_extract_count: typeof newValue === 'string' ? 'Any' : newValue,
                  });
                }}
                w={'50%'}
                error={!!extractInvalidExtractCount}
              />
              <StyledNumberInput
                label={
                  <InputLabelWithHoverCard
                    label={'Max Extraction Items'}
                    hoverText={
                      'The maximum number of items to extract. Leave empty to let system decide'
                    }
                  />
                }
                placeholder="Any"
                value={fields.max_extract_count === 'Any' ? '' : fields.max_extract_count}
                onChange={(newValue) => {
                  onChangeWithDetectConfigNormalization({
                    max_extract_count: typeof newValue === 'string' ? 'Any' : newValue,
                  });
                }}
                w={'50%'}
                error={!!extractInvalidExtractCount}
              />
            </Group>
            <StyledInputErrorText>{extractInvalidExtractCount}</StyledInputErrorText>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default ExtractMetadataFields;
